import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollText, ShieldCheck, List, GitCommitVertical } from 'lucide-react';
import { auditAPI } from '../lib/api';
import { DataTable } from '../components/ds/DataTable';
import { EmptyState, PageHeader } from '../components/ds/Primitives';

const tone = (a = '') => (a.includes('deleted') || a.includes('denied') || a.includes('failed') ? 'bg-destructive/10 text-destructive'
  : a.includes('created') ? 'bg-success/15 text-success' : a.includes('updated') ? 'bg-warning/15 text-warning' : a.includes('sent') ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground');
const ACR = { bas: 'BAS', gst: 'GST', itc: 'ITC', ato: 'ATO', aml: 'AML', mfa: 'MFA', ai: 'AI', abn: 'ABN', tfn: 'TFN', payg: 'PAYG', ias: 'IAS', sbr: 'SBR', ip: 'IP', esign: 'E-sign', authz: 'Access', kyc: 'KYC', dsp: 'DSP', xpm: 'XPM' };
const label = (a = '') => { const s = a.replace(/[._]/g, ' ').trim().split(/\s+/).map((w) => ACR[w.toLowerCase()] || w).join(' '); return s.charAt(0).toUpperCase() + s.slice(1); };
const rec = (t = '') => label(t || 'record');
const fmt = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('en-AU'); } catch { return iso; } };
const ActionPill = ({ a }) => <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${tone(a)}`}><span className="h-1.5 w-1.5 rounded-full bg-current" />{label(a)}</span>;

function ChangesCell({ changes }) {
  if (!changes || Object.keys(changes).length === 0) return <span className="text-muted-foreground/70">—</span>;
  return (
    <div className="space-y-1">
      {Object.entries(changes).slice(0, 4).map(([field, v]) => (
        <div key={field} className="text-xs">
          <span className="font-medium text-foreground/90">{label(field)}:</span>{' '}
          <span className="text-destructive line-through">{String(v.from ?? '∅').slice(0, 24)}</span>{' '}
          <span className="text-muted-foreground/70">→</span>{' '}
          <span className="text-success">{String(v.to ?? '∅').slice(0, 24)}</span>
        </div>
      ))}
      {Object.keys(changes).length > 4 && <div className="text-xs text-muted-foreground/70">+{Object.keys(changes).length - 4} more</div>}
    </div>
  );
}

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [failed, setFailed] = useState(false);
  const [targetType, setTargetType] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [mode, setMode] = useState('table');

  useEffect(() => {
    setLoading(true); setFailed(false);
    const params = { limit: 100 };
    if (targetType !== 'all') params.target_type = targetType;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    auditAPI.list(params).then((res) => { setLogs(res.data.logs || []); setTotal(res.data.total || 0); })
      .catch((err) => (err.response?.status === 403 ? setDenied(true) : setFailed(true))).finally(() => setLoading(false));
  }, [targetType, dateFrom, dateTo]);

  const columns = [
    { key: 'created_at', header: 'When', sortable: true, cell: (l) => <span className="whitespace-nowrap text-xs tabular">{fmt(l.created_at)}</span> },
    { key: 'actor_name', header: 'Who', sortable: true, cell: (l) => l.actor_name || <span className="text-muted-foreground">{l.actor_id ? 'Former user' : 'System'}</span> },
    { key: 'action', header: 'Action', cell: (l) => <ActionPill a={l.action} /> },
    { key: 'target_type', header: 'Record', cell: (l) => rec(l.target_type) },
    { key: 'changes', header: 'Changes (before → after)', cell: (l) => <ChangesCell changes={l.changes} /> },
    { key: 'ip_address', header: 'IP', cell: (l) => <span className="font-mono text-xs text-muted-foreground">{l.ip_address || '—'}</span> },
  ];

  if (denied) return <div className="p-4 sm:p-6 lg:p-8"><EmptyState icon={ShieldCheck} title="Audit log is restricted" testId="audit-denied">Only practice owners and admins can view the audit log.</EmptyState></div>;
  return (
    <div className="space-y-5 p-4 sm:p-6 lg:p-8">
      <PageHeader title={<span data-testid="audit-log-title">Audit log</span>} description="A tamper-proof record of who changed what, and when, with before/after values. Read-only." />
      <div className="flex flex-wrap items-end gap-2" data-testid="audit-filters">
        <Select value={targetType} onValueChange={setTargetType}>
          <SelectTrigger className="w-44" data-testid="filter-target-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All records</SelectItem><SelectItem value="client">Clients</SelectItem><SelectItem value="job">Jobs</SelectItem>
            <SelectItem value="user">Users</SelectItem><SelectItem value="compliance_reminder">Reminders</SelectItem>
          </SelectContent>
        </Select>
        <label className="text-xs text-muted-foreground">From<Input type="date" className="w-40" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" /></label>
        <label className="text-xs text-muted-foreground">To<Input type="date" className="w-40" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" /></label>
        <Button variant="ghost" onClick={() => { setTargetType('all'); setDateFrom(''); setDateTo(''); }} data-testid="audit-clear">Clear</Button>
        <div className="ml-auto flex gap-1">
          <Button size="sm" variant={mode === 'table' ? 'default' : 'outline'} onClick={() => setMode('table')} data-testid="audit-mode-table"><List className="mr-1 h-4 w-4" />Table</Button>
          <Button size="sm" variant={mode === 'timeline' ? 'default' : 'outline'} onClick={() => setMode('timeline')} data-testid="audit-mode-timeline"><GitCommitVertical className="mr-1 h-4 w-4" />Timeline</Button>
        </div>
      </div>
      {loading ? <div className="space-y-2 rounded-lg border border-border bg-card p-4" data-testid="audit-loading">{[0, 1, 2, 3, 4].map((i) => <div key={i} className="h-8 animate-pulse rounded bg-muted" />)}</div>
        : failed ? <p className="text-sm text-destructive" data-testid="audit-error">The audit log couldn’t be loaded. Refresh to try again.</p>
          : !logs.length ? <EmptyState icon={ScrollText} title="No audit entries" testId="audit-empty">Nothing matches the selected filters.</EmptyState>
            : mode === 'table' ? <DataTable testId="audit-table" storageKey="audit" rows={logs} columns={columns} />
              : <ol className="relative space-y-4 border-l border-border pl-5" data-testid="audit-timeline">{logs.map((l) => (
                <li key={l.id} className="relative" data-testid={`audit-tl-${l.id}`}>
                  <span className="absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-card bg-primary" />
                  <div className="flex flex-wrap items-center gap-2 text-sm"><ActionPill a={l.action} /><span className="text-muted-foreground">{rec(l.target_type)}</span><span className="text-xs text-muted-foreground tabular">{fmt(l.created_at)}</span></div>
                  <p className="text-sm">{l.actor_name || 'System'}</p><ChangesCell changes={l.changes} />
                </li>))}</ol>}
      <p className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="audit-footer"><ShieldCheck className="h-4 w-4" />Append-only: records can never be edited or deleted. Showing {logs.length} of {total} entries (most recent 100).</p>
    </div>
  );
}
